
import React, { useState } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import type { User, Subscription, Language } from '../types';
import UsersIcon from './icons/UsersIcon';
import XIcon from './icons/XIcon';
import ApproveSubscriptionModal from './ApproveSubscriptionModal';
import { getTranslation } from '../utils/translations';

interface SubscriptionManagementModalProps {
    isOpen: boolean;
    onClose: () => void;
    users: User[];
    language?: Language;
}

const SubscriptionManagementModal: React.FC<SubscriptionManagementModalProps> = ({
    isOpen,
    onClose,
    users,
    language = 'he',
}) => {
    const t = (key: Parameters<typeof getTranslation>[1]) => getTranslation(language, key);
    const [approveTarget, setApproveTarget] = useState<User | null>(null);
    const [decliningUid, setDecliningUid] = useState<string | null>(null);

    if (!isOpen) return null;

    const newUsers = users.filter(
        (u) => u.subscription && u.subscription.adminViewed === false && u.role !== 'superuser'
    );

    const handleDecline = async (uid: string) => {
        setDecliningUid(uid);
        try {
            const userRef = doc(db, 'users', uid);
            const userSnap = await getDoc(userRef);
            if (!userSnap.exists()) return;
            const userData = userSnap.data() as User;
            if (!userData.subscription) return;

            const subscription: Subscription = {
                ...userData.subscription,
                status: 'declined',
                adminViewed: true,
            };
            await updateDoc(userRef, { subscription });
        } catch (error) {
            console.error('Failed to decline subscription', error);
            alert(t('errorSaving'));
        } finally {
            setDecliningUid(null);
        }
    };

    const getStatusLabel = (status?: string) => {
        if (status === 'pending') return t('pending');
        if (status === 'subscribed') return t('subscribed');
        if (status === 'declined') return t('declined');
        if (status === 'canceled') return t('canceled');
        return status || '-';
    };

    return (
        <>
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col transition-colors">
                    <div className="p-6 border-b flex justify-between items-center bg-purple-50 dark:bg-purple-900/30 rounded-t-xl dark:border-slate-700">
                        <div className="flex items-center gap-2 text-purple-900 dark:text-purple-200">
                            <UsersIcon className="h-6 w-6" />
                            <h2 className="text-xl font-bold">{t('approveNewSubscriptions')}</h2>
                        </div>
                        <button onClick={onClose} className="text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 p-1">
                            <XIcon className="h-6 w-6" />
                        </button>
                    </div>

                    <div className="p-6 overflow-y-auto flex-grow bg-slate-50 dark:bg-slate-800/30">
                        {newUsers.length === 0 ? (
                            <div className="text-center py-12 text-slate-500 dark:text-slate-400">
                                <p>{t('noPendingSubscriptions')}</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm">
                                <table className="w-full text-sm text-right bg-white dark:bg-slate-900">
                                    <thead className="text-xs text-slate-500 dark:text-slate-400 uppercase bg-slate-100 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                                        <tr>
                                            <th className="px-6 py-3">{t('user')}</th>
                                            <th className="px-6 py-3">{t('email')}</th>
                                            <th className="px-6 py-3">{t('currentStatus')}</th>
                                            <th className="px-6 py-3 text-center">{t('actions')}</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                        {newUsers.map((user) => (
                                            <tr key={user.uid} className="hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                                                <td className="px-6 py-4 font-medium text-slate-900 dark:text-slate-100">
                                                    {user.nickname || `${user.firstName || ''} ${user.lastName || ''}`.trim() || '-'}
                                                </td>
                                                <td className="px-6 py-4 text-slate-600 dark:text-slate-300 dir-ltr text-right">
                                                    {user.email}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span
                                                        className={`px-2 py-1 rounded text-xs font-bold ${
                                                            user.subscription?.status === 'pending'
                                                                ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                                                                : user.subscription?.status === 'subscribed'
                                                                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                                                                    : 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300'
                                                        }`}
                                                    >
                                                        {getStatusLabel(user.subscription?.status)}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex justify-center gap-2">
                                                        <button
                                                            type="button"
                                                            onClick={() => setApproveTarget(user)}
                                                            className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-xs font-medium shadow-sm transition-colors"
                                                        >
                                                            {t('approveSubscription')}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => void handleDecline(user.uid)}
                                                            disabled={decliningUid === user.uid}
                                                            className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-xs font-medium shadow-sm transition-colors disabled:opacity-50"
                                                        >
                                                            {decliningUid === user.uid ? '...' : t('decline')}
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    <div className="p-4 bg-slate-50 dark:bg-slate-800 border-t dark:border-slate-700 flex justify-end">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-md hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors"
                        >
                            {t('close')}
                        </button>
                    </div>
                </div>
            </div>

            <ApproveSubscriptionModal
                user={approveTarget}
                language={language}
                onClose={() => setApproveTarget(null)}
            />
        </>
    );
};

export default SubscriptionManagementModal;
